export const SUPPLY_CHAIN_RISK_SCORE_RANGE = {
   min: 1,
   max: 5,
   step: 0.1,
} as const;

export interface SupplyChainRiskClassificationScale {
   mediumThreshold: number;
   highThreshold: number;
}

export const DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE = {
   mediumThreshold: 2,
   highThreshold: 3.5,
} as const satisfies SupplyChainRiskClassificationScale;

export const SUPPLY_CHAIN_RISK_BANDS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type SupplyChainRiskBand = (typeof SUPPLY_CHAIN_RISK_BANDS)[number];

const SUPPLY_CHAIN_RISK_BAND_DEFINITIONS: Record<
   SupplyChainRiskBand,
   {
      label: 'Low' | 'Medium' | 'High';
      color: 'green' | 'yellow' | 'red';
      muiColor: 'success' | 'warning' | 'error';
      labelKey: string;
   }
> = {
   LOW: {
      label: 'Low',
      color: 'green',
      muiColor: 'success',
      labelKey: 'supplyChain.abstractRiskAssessment.riskLevel.low',
   },
   MEDIUM: {
      label: 'Medium',
      color: 'yellow',
      muiColor: 'warning',
      labelKey: 'supplyChain.abstractRiskAssessment.riskLevel.medium',
   },
   HIGH: {
      label: 'High',
      color: 'red',
      muiColor: 'error',
      labelKey: 'supplyChain.abstractRiskAssessment.riskLevel.high',
   },
};

const SCORE_COMPARISON_EPSILON = 1e-9;
const SCORE_PRECISION_MULTIPLIER = Math.round(1 / SUPPLY_CHAIN_RISK_SCORE_RANGE.step);
const getScoreTick = (value: number) => Math.round((value - SUPPLY_CHAIN_RISK_SCORE_RANGE.min) / SUPPLY_CHAIN_RISK_SCORE_RANGE.step);
const getScoreAtTick = (tick: number) => SUPPLY_CHAIN_RISK_SCORE_RANGE.min + tick * SUPPLY_CHAIN_RISK_SCORE_RANGE.step;
const getCanonicalScore = (value: number) => Math.round(value * SCORE_PRECISION_MULTIPLIER) / SCORE_PRECISION_MULTIPLIER;
const isStepAligned = (value: number) => Math.abs(value - getScoreAtTick(getScoreTick(value))) < SCORE_COMPARISON_EPSILON;

export function isSupplyChainRiskClassificationScale(value: unknown): value is SupplyChainRiskClassificationScale {
   if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

   const { mediumThreshold, highThreshold } = value as Record<string, unknown>;
   if (typeof mediumThreshold !== 'number' || !Number.isFinite(mediumThreshold) || !isStepAligned(mediumThreshold)) return false;
   if (typeof highThreshold !== 'number' || !Number.isFinite(highThreshold) || !isStepAligned(highThreshold)) return false;

   const mediumTick = getScoreTick(mediumThreshold);
   const highTick = getScoreTick(highThreshold);
   const maximumTick = getScoreTick(SUPPLY_CHAIN_RISK_SCORE_RANGE.max);

   return mediumTick >= 1 && highTick >= mediumTick + 1 && highTick <= maximumTick;
}

export function classifySupplyChainRiskScore(
   score: number | null | undefined,
   scale: SupplyChainRiskClassificationScale = DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE
): SupplyChainRiskBand | null {
   if (!isSupplyChainRiskClassificationScale(scale)) throw new RangeError('Invalid Supply Chain Risk Classification Scale');
   if (typeof score !== 'number' || !Number.isFinite(score)) return null;
   if (score < SUPPLY_CHAIN_RISK_SCORE_RANGE.min || score > SUPPLY_CHAIN_RISK_SCORE_RANGE.max) return null;

   if (score >= getCanonicalScore(scale.highThreshold)) return 'HIGH';
   if (score >= getCanonicalScore(scale.mediumThreshold)) return 'MEDIUM';
   return 'LOW';
}

export function getSupplyChainRiskBandDefinition(band: SupplyChainRiskBand) {
   return SUPPLY_CHAIN_RISK_BAND_DEFINITIONS[band];
}

export interface SupplyChainRiskBandRange {
   band: SupplyChainRiskBand;
   minInclusive: number;
   maxInclusive: number;
}

export interface SupplyChainRiskBandBounds {
   minInclusive: number;
   maxExclusive: number | null;
   maxInclusive: number | null;
}

export function getSupplyChainRiskBandBounds(
   band: SupplyChainRiskBand,
   scale: SupplyChainRiskClassificationScale = DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE
): SupplyChainRiskBandBounds {
   const ranges = getSupplyChainRiskBandRanges(scale);
   const bandIndex = SUPPLY_CHAIN_RISK_BANDS.indexOf(band);
   const range = ranges[bandIndex];
   const nextRange = ranges[bandIndex + 1];

   return {
      minInclusive: range.minInclusive,
      maxExclusive: nextRange?.minInclusive ?? null,
      maxInclusive: nextRange ? null : range.maxInclusive,
   };
}

export function getSupplyChainRiskBandRanges(
   scale: SupplyChainRiskClassificationScale = DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE
): SupplyChainRiskBandRange[] {
   if (!isSupplyChainRiskClassificationScale(scale)) throw new RangeError('Invalid Supply Chain Risk Classification Scale');

   return [
      {
         band: 'LOW',
         minInclusive: SUPPLY_CHAIN_RISK_SCORE_RANGE.min,
         maxInclusive: getCanonicalScore(scale.mediumThreshold - SUPPLY_CHAIN_RISK_SCORE_RANGE.step),
      },
      {
         band: 'MEDIUM',
         minInclusive: getCanonicalScore(scale.mediumThreshold),
         maxInclusive: getCanonicalScore(scale.highThreshold - SUPPLY_CHAIN_RISK_SCORE_RANGE.step),
      },
      {
         band: 'HIGH',
         minInclusive: getCanonicalScore(scale.highThreshold),
         maxInclusive: SUPPLY_CHAIN_RISK_SCORE_RANGE.max,
      },
   ];
}
